import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import nth from "lodash/nth";
import type { FunctionComponent } from "react";
import { Suspense, useEffect } from "react";
import { graphql, useFragment, useQueryLoader } from "react-relay";

import type { issuesNewsletterArticleArticle$key } from "~/queries/__generated__/issuesNewsletterArticleArticle.graphql";
import type { issuesNewsletterArticleQuery } from "~/queries/__generated__/issuesNewsletterArticleQuery.graphql";

import { NewsletterArticleIssuesDropdown } from "./issues-dropdown";

type Props = {
  content: issuesNewsletterArticleArticle$key;
};

export const query = graphql`
  query issuesNewsletterArticleQuery($typeIds: [String]!, $first: Int!) {
    articles(
      filter: { typeIds: $typeIds }
      orderBy: { field: PUBLISHED_DATE, direction: DESC }
      first: $first
    ) {
      ...issuesDropdownNewsletterArticleArticleConnection
    }
  }
`;

export const NewsletterArticleIssues: FunctionComponent<Props> = ({ content: content_ }) => {
  const article = useFragment(
    graphql`
      fragment issuesNewsletterArticleArticle on Article {
        types {
          value {
            entityUuid
          }
        }
        ...issuesDropdownNewsletterArticleArticle
      }
    `,
    content_,
  );

  const articleTypeIds = first(article.types?.filter(notEmpty))
    ?.value?.filter(notEmpty)
    .map(type => type?.entityUuid);
  const articleTypeId = nth(articleTypeIds, 1);

  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<issuesNewsletterArticleQuery>(query);

  useEffect(() => {
    if (!articleTypeId) return;
    loadQuery({
      first: 10,
      typeIds: [articleTypeId],
    });

    return disposeQuery;
  }, [disposeQuery, loadQuery, articleTypeId]);

  return (
    <div>
      <Suspense fallback={<></>}>
        {queryReference && (
          <NewsletterArticleIssuesDropdown content={article} queryReference={queryReference} />
        )}
      </Suspense>
    </div>
  );
};

NewsletterArticleIssues.displayName = "NewsletterArticleIssues";
